/**
 * usePresence — wires the presence Redux slice to live WebSocket frames.
 *
 * Two server-pushed frames keep the slice in sync; the hook stays fully
 * socket-driven and never polls REST:
 *
 *   {type:"presence_snapshot", rows:[…]}  — one-shot, right after the
 *                                            "connected" frame; hydrates
 *                                            the whole branch in bulk.
 *   {type:"presence", user_id, status}    — incremental flips, broadcast
 *                                            tenant-wide on every
 *                                            connect/disconnect/away/
 *                                            on_leave transition.
 *
 * Parents call `handlePresenceEvent(event)` from the chat WS dispatcher.
 * The hook also exposes `hydrate()` for components that need a manual
 * REST pull (e.g. Employees.tsx); this is no longer used by Chat itself.
 */
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import api from '../../api/api';
import {
  bulkHydrate,
  setPresence,
  PresenceStatus,
} from '../../redux/slices/presence';

// REST fallback — retained for components outside the chat tree that don't
// have a WS subscription. The chat page itself uses the WS-pushed snapshot.
const fetchPresenceSnapshot = async (): Promise<
  Array<{ user_id: string; status: PresenceStatus; leave_end_date?: string | null }>
> => {
  try {
    const { data } = await api.get('/presence/snapshot');
    const rows = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
    return rows
      .filter((r: any) => r && r.user_id)
      .map((r: any) => ({
        user_id: String(r.user_id),
        status: (r.status || 'offline') as PresenceStatus,
        leave_end_date: r.leave_end_date ?? null,
      }));
  } catch {
    return [];
  }
};

interface PresenceWsFrame {
  type: 'presence';
  user_id: string;
  status: PresenceStatus;
  leave_end_date?: string | null;
}

interface PresenceSnapshotFrame {
  type: 'presence_snapshot';
  rows: Array<{ user_id: string; status: PresenceStatus; leave_end_date?: string | null }>;
}

export function usePresence() {
  const dispatch = useDispatch();

  const hydrate = useCallback(async () => {
    const rows = await fetchPresenceSnapshot();
    // REST fallback may return a filtered subset — don't let it prune users.
    dispatch(bulkHydrate({ full: false, rows: rows.map((r) => ({ ...r, leave_end_date: r.leave_end_date ?? null })) }));
  }, [dispatch]);

  const handlePresenceEvent = useCallback(
    (event: any) => {
      if (!event) return;
      if (event.type === 'presence_snapshot') {
        const f = event as PresenceSnapshotFrame;
        if (!Array.isArray(f.rows)) return;
        dispatch(
          bulkHydrate({
            rows: f.rows
              .filter((r) => r && r.user_id && r.status)
              .map((r) => ({
                user_id: String(r.user_id),
                status: r.status,
                leave_end_date: r.leave_end_date ?? null,
              })),
          }),
        );
        return;
      }
      if (event.type !== 'presence') return;
      const f = event as PresenceWsFrame;
      if (!f.user_id || !f.status) return;
      dispatch(
        setPresence({
          userId: f.user_id,
          status: f.status,
          leaveEndDate: f.leave_end_date ?? null,
        }),
      );
    },
    [dispatch],
  );

  return { hydrate, handlePresenceEvent };
}

export default usePresence;
